/**
 * BookCover.tsx — 书籍真实封面 + 渐变回退
 *
 * 逐字搬自原型 design/Novel-admin/src/primitives.jsx 的 BookCover：
 * - 绝对 URL 直接显示；共享桶相对 ref 经后台鉴权代理取 blob；
 * - URL 缺失、读取失败或图片解码失败时按 hue 回退 coverBg；
 * - 高度缺省时按 round(width*1.37) 推导（书封纵横比）；
 * - position:relative + overflow:hidden 供内部角标 / 蒙层定位。
 * 默认值（hue=0 / width=40 / radius=6）与各调用方 1:1 保真。
 */

import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { authedFetch } from '~/api/session-refresh';
import { newRequestId } from '~/lib/request-id';
import { coverBg } from '~/ui/visual-helpers';

interface BookCoverProps {
  /** 真实封面 URL：绝对 http(s) URL 或 /api/v1/static/covers/:id/cover.png 相对 ref */
  src?: string | null;
  /** 图片替代文本；装饰性封面可留空 */
  alt?: string;
  /** 封面色相（0~359），默认 0 */
  hue?: number;
  /** 宽度像素，默认 40 */
  width?: number;
  /** 高度像素；缺省时按 round(width*1.37) 推导 */
  height?: number;
  /** 圆角，默认 6 */
  radius?: number;
  /** 叠加内容（角标 / 蒙层等） */
  children?: ReactNode;
  /** 透传外层样式覆盖 */
  style?: CSSProperties;
}

export function BookCover({
  src,
  alt = '',
  hue = 0,
  width = 40,
  height,
  radius = 6,
  children,
  style,
}: BookCoverProps) {
  const h = height !== undefined ? height : Math.round(width * 1.37);
  const [proxiedSrc, setProxiedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const relativeMatch = src?.match(/^\/api\/v1\/static\/covers\/([0-9a-f-]{36})\/cover\.png$/);
  const coverAssetId = relativeMatch?.[1];
  const directSrc = src && !relativeMatch ? src : null;

  useEffect(() => {
    setProxiedSrc(null);
    if (!coverAssetId) return;

    let cancelled = false;
    let objectUrl: string | null = null;
    const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
    void authedFetch(`${apiBase}/api/v1/books/covers/${coverAssetId}/content`, {
      method: 'GET',
      headers: { 'X-Request-Id': newRequestId() },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('cover fetch failed');
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setProxiedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setFailedSrc(`/api/v1/static/covers/${coverAssetId}/cover.png`);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [coverAssetId]);

  const failed = src != null && failedSrc === src;
  const imageSrc = failed ? null : (proxiedSrc ?? directSrc);
  return (
    <div
      style={{
        width,
        height: h,
        flexShrink: 0,
        borderRadius: radius,
        background: coverBg(hue),
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={alt}
          onError={() => setFailedSrc(src ?? null)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        children
      )}
    </div>
  );
}
